import { PayloadAction } from '@reduxjs/toolkit'
import { ProcessingJob, ProcessingJobStatusStatistics } from 'features/jobs/types'
import { User } from 'features/users'
import { all, call, select, takeEvery } from 'redux-saga/effects'
import {
  accessInEvent,
  accessInRequestEvent,
  createDOMapEvent,
  createMetricEvent,
  createTilesetEvent,
  launchGeocodeTableEvent,
  MetricEventType,
  postQuotaEventRequest,
  onboardingItemCheckedEvent,
  featuredUpdateOpenedEvent,
  demoMapDuplicatedEvent,
  demoWorkflowDuplicatedEvent,
  analysisCreatedEvent,
  enrichmentCreatedEvent
} from '../api/metrics'
import {
  DatasetIdProviderMetric,
  GeocodeMetric,
  QuotaEventMetric,
  OnboardingItemMetric,
  AnalysisCreatedEvent
} from '../types'
import {
  accessInMetricRequest,
  createDOMapMetricRequest,
  createMetricRequest,
  createTilesetMetricRequest,
  launchGeocodeTableMetricRequest,
  quotaEventRequest,
  requestAccessInMetricRequest,
  onboardingItemCheckedEventRequest,
  featuredUpdateOpenedEventRequest,
  demoMapDuplicatedEventRequest,
  demoWorkflowDuplicatedEventRequest,
  analysisCreatedEventRequest,
  createEnrichMetricRequest
} from './metricsSlice'

function* createDOMapMetric(action: PayloadAction<DatasetIdProviderMetric>) {
  try {
    const user: User = yield select((state) => state.users.me)
    yield call(createDOMapEvent, { user, ...action.payload })
  } catch (e) {
    console.error(e)
  }
}

function* createTilesetMetric(action: PayloadAction<DatasetIdProviderMetric & { advancedMode; connectionId }>) {
  try {
    const user: User = yield select((state) => state.users.me)
    yield call(createTilesetEvent, { user, ...action.payload })
  } catch (e) {
    console.error(e)
  }
}

function* accessInMetric(action: PayloadAction<DatasetIdProviderMetric>) {
  try {
    const user: User = yield select((state) => state.users.me)
    yield call(accessInEvent, { user, ...action.payload })
  } catch (e) {
    console.error(e)
  }
}

function* requestAccessInMetric(action: PayloadAction<DatasetIdProviderMetric>) {
  try {
    const user: User = yield select((state) => state.users.me)
    yield call(accessInRequestEvent, { user, ...action.payload })
  } catch (e) {
    console.error(e)
  }
}

function* createMetric(action: PayloadAction<MetricEventType>) {
  try {
    const user: User = yield select((state) => state.users.me)
    const targetEvent = createMetricEvent(action.payload)
    yield call(targetEvent, { user })
  } catch (e) {
    console.error(e)
  }
}

function* launchGeocodeTableMetric(action: PayloadAction<GeocodeMetric>) {
  try {
    const user: User = yield select((state) => state.users.me)
    yield call(launchGeocodeTableEvent, { user, ...action.payload })
  } catch (e) {
    console.error(e)
  }
}

function* handleQuotaEventRequest(action: PayloadAction<QuotaEventMetric>) {
  try {
    const user: User = yield select((state) => state.users.me)
    yield call(postQuotaEventRequest, { user, ...action.payload })
  } catch (e) {
    console.error(e)
  }
}

function* onboardingItemCheckedMetric(action: PayloadAction<OnboardingItemMetric>) {
  try {
    const user: User = yield select((state) => state.users.me)
    yield call(onboardingItemCheckedEvent, { user, ...action.payload })
  } catch (e) {
    console.error(e)
  }
}

function* featuredUpdateOpenedMetric(action: PayloadAction<OnboardingItemMetric>) {
  try {
    const user: User = yield select((state) => state.users.me)
    yield call(featuredUpdateOpenedEvent, { user, ...action.payload })
  } catch (e) {
    console.error(e)
  }
}

function* demoMapDuplicatedMetric(action: PayloadAction<OnboardingItemMetric>) {
  try {
    const user: User = yield select((state) => state.users.me)
    yield call(demoMapDuplicatedEvent, { user, ...action.payload })
  } catch (e) {
    console.error(e)
  }
}

function* demoWorkflowDuplicatedMetric(action: PayloadAction<OnboardingItemMetric>) {
  try {
    const user: User = yield select((state) => state.users.me)
    yield call(demoWorkflowDuplicatedEvent, { user, ...action.payload })
  } catch (e) {
    console.error(e)
  }
}

function* analysisCreatedMetric(action: PayloadAction<AnalysisCreatedEvent>) {
  try {
    const user: User = yield select((state) => state.users.me)
    yield call(analysisCreatedEvent, { user, ...action.payload })
  } catch (e) {
    console.error(e)
  }
}

function* createEnrichMetric(
  action: PayloadAction<{ job: ProcessingJob; statistics: ProcessingJobStatusStatistics | null; isCompleted: string }>
) {
  try {
    const { job, statistics, isCompleted } = action.payload
    const account = yield select((state) => state.account.data)
    const user: User = yield select((state) => state.users.me)
    const data = {
      userId: user.user_id,
      accountId: account.id,
      process_completed: isCompleted,
      process_duration: statistics ? Number(statistics.endTime) - Number(statistics.startTime) : null
    }
    yield call(enrichmentCreatedEvent, { user, ...data, ...job.event?.payload })
  } catch (e) {
    console.error(e)
  }
}

function* watchFetchMetrics() {
  yield takeEvery(createDOMapMetricRequest.type, createDOMapMetric)
  yield takeEvery(createTilesetMetricRequest.type, createTilesetMetric)
  yield takeEvery(requestAccessInMetricRequest.type, requestAccessInMetric)
  yield takeEvery(accessInMetricRequest.type, accessInMetric)
  yield takeEvery(createMetricRequest.type, createMetric)
  yield takeEvery(launchGeocodeTableMetricRequest.type, launchGeocodeTableMetric)
  yield takeEvery(quotaEventRequest.type, handleQuotaEventRequest)
  yield takeEvery(onboardingItemCheckedEventRequest.type, onboardingItemCheckedMetric)
  yield takeEvery(featuredUpdateOpenedEventRequest.type, featuredUpdateOpenedMetric)
  yield takeEvery(demoMapDuplicatedEventRequest.type, demoMapDuplicatedMetric)
  yield takeEvery(demoWorkflowDuplicatedEventRequest.type, demoWorkflowDuplicatedMetric)
  yield takeEvery(analysisCreatedEventRequest.type, analysisCreatedMetric)
  yield takeEvery(createEnrichMetricRequest.type, createEnrichMetric)
}

export default function* jobsImportingSaga() {
  try {
    yield all([watchFetchMetrics()])
  } catch (e) {
    //TODO
  }
}
